import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';

import { isNotNullOrUndefined } from '../../helpers/typed-reactive-filters.helper';
import { TenantInfoDataService } from '../data-services/tenant-info.data-service';

@Injectable({ providedIn: 'root' })
export class StylingService {
    private organisationName$: Observable<string>;
    private faviconUrl$: Observable<string>;
    private logoUrl$: Observable<string>;
    private styleUrl$: Observable<string>;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private httpClient: HttpClient,
        private tenantInfoDataService: TenantInfoDataService
    ) {
        this.initOrganisationName();

        this.setCustomerFavicon();
        this.setCustomerPngLogo();
        this.setCustomerSvgLogo();
        this.setCustomerStyle();
    }

    private initOrganisationName(): void {
        this.organisationName$ = this.tenantInfoDataService.getTenantInfo().pipe(
            map(tenantInfo => tenantInfo.OrganisationName),
            take(1),
            shareReplay(1)
        );
    }

    // #region Favicon
    private initCustomerFaviconUrl(): Observable<string> {
        return (this.faviconUrl$ = this.organisationName$.pipe(
            map(organisationName => `customer-styles/${organisationName}/favicon.ico`),
            switchMap(faviconUrl =>
                this.checkFileExists(faviconUrl).pipe(map(exists => (exists ? faviconUrl : undefined)))
            ),
            isNotNullOrUndefined(),
            shareReplay(1)
        ));
    }

    public getCustomerFaviconUrl(): Observable<string> {
        return this.faviconUrl$ ? this.faviconUrl$ : this.initCustomerFaviconUrl();
    }

    private setCustomerFavicon(): void {
        this.getCustomerFaviconUrl().subscribe(faviconUrl => {
            if (faviconUrl) {
                const faviconElements = this.document.getElementsByName('logo-ico');

                faviconElements.forEach(faviconEl => {
                    faviconEl.setAttribute('href', faviconUrl);
                });
            }
        });
    }
    // #endregion

    // #region Logo
    private initCustomerLogoUrl(): Observable<string> {
        return (this.logoUrl$ = this.logoUrl$ =
            this.organisationName$.pipe(map(organisationName => `customer-styles/${organisationName}/logo`)));
    }

    private getCustomerLogoUrl(): Observable<string> {
        return this.logoUrl$ ? this.logoUrl$ : this.initCustomerLogoUrl();
    }

    public getCustomerSvgLogoUrl(): Observable<string | undefined> {
        return this.getCustomerLogoUrl().pipe(
            map(partialUrl => `${partialUrl}.svg`),
            switchMap(logoUrl => this.checkFileExists(logoUrl).pipe(map(exists => (exists ? logoUrl : undefined)))),
            shareReplay(1)
        );
    }

    private setCustomerSvgLogo(): void {
        this.getCustomerSvgLogoUrl()
            .pipe(isNotNullOrUndefined())
            .subscribe(logoUrl => {
                this.setHrefOfNamedElements('logo-svg', logoUrl);
            });
    }

    public getCustomerPngLogoUrl(): Observable<string | undefined> {
        return this.getCustomerLogoUrl().pipe(
            map(partialUrl => `${partialUrl}.png`),
            switchMap(logoUrl => this.checkFileExists(logoUrl).pipe(map(exists => (exists ? logoUrl : undefined)))),
            shareReplay(1)
        );
    }

    private setCustomerPngLogo(): void {
        this.getCustomerPngLogoUrl()
            .pipe(isNotNullOrUndefined())
            .subscribe(logoUrl => {
                this.setHrefOfNamedElements('logo-png', logoUrl);
            });
    }
    // #endregion

    // #region Style
    private initCustomerStyleUrl(): Observable<string> {
        return (this.styleUrl$ = this.organisationName$.pipe(
            map(organisationName => `customer-styles/${organisationName}/${organisationName}.css`),
            switchMap(styleUrl => this.checkFileExists(styleUrl).pipe(map(exists => (exists ? styleUrl : undefined)))),
            isNotNullOrUndefined(),
            shareReplay(1)
        ));
    }

    private getCustomerStyleUrl(): Observable<string> {
        return this.styleUrl$ ? this.styleUrl$ : this.initCustomerStyleUrl();
    }

    private setCustomerStyle(): void {
        this.getCustomerStyleUrl().subscribe(styleUrl => {
            if (styleUrl) {
                const styleLink: HTMLLinkElement = this.document.createElement('link');
                styleLink.rel = 'stylesheet';
                styleLink.type = 'text/css';
                styleLink.href = styleUrl;
                this.document.head.appendChild(styleLink);
            }
        });
    }
    // #endregion

    // #region Utils
    private checkFileExists(url: string): Observable<boolean> {
        return this.httpClient.get(url, { observe: 'response', responseType: 'blob' }).pipe(
            map(response => {
                return true;
            }),
            catchError(error => {
                return of(false);
            })
        );
    }

    private setHrefOfNamedElements(name: string, href: string): void {
        const namedElements = this.document.getElementsByName(name);

        namedElements.forEach(el => el.setAttribute('href', href));
    }
    // #endregion
}
